import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import api from "@/lib/api";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { MapTrifold, Truck, Leaf, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [regOpen, setRegOpen] = useState(false);
  const [regBusy, setRegBusy] = useState(false);
  const emptyReg = { company_name: "", plant_location: "", admin_name: "", admin_email: "", admin_password: "" };
  const [reg, setReg] = useState(emptyReg);
  const [recOpen, setRecOpen] = useState(false);
  const [recBusy, setRecBusy] = useState(false);
  const emptyRec = { name: "", email: "", password: "" };
  const [rec, setRec] = useState(emptyRec);
  const navigate = useNavigate();
  useDocumentTitle("Sign In");

  if (user) return <Navigate to="/" replace />;

  const dest = (u) =>
    u.role === "driver" ? "/driver" : u.role === "receiver" ? "/receive" : u.role === "superadmin" ? "/tenants" : "/control-tower";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome back, ${u.name}`);
      navigate(dest(u));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const submitReg = async () => {
    if (!reg.company_name || !reg.admin_email || !reg.admin_password) return toast.error("Fill in all fields");
    setRegBusy(true);
    try {
      const { data } = await api.post("/auth/register-company", reg);
      toast.success(data.message || "Registration submitted for approval", { duration: 6000 });
      setRegOpen(false);
      setReg(emptyReg);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally {
      setRegBusy(false);
    }
  };

  const submitRec = async () => {
    if (!rec.name || !rec.email || !rec.password) return toast.error("Fill in all fields");
    setRecBusy(true);
    try {
      await api.post("/auth/register", { name: rec.name, email: rec.email.trim(), password: rec.password });
      const u = await login(rec.email.trim(), rec.password);
      setRecOpen(false);
      setRec(emptyRec);
      toast.success(`Welcome, ${u.name}`);
      navigate("/receive");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally {
      setRecBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12 overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "url(https://images.pexels.com/photos/16180770/pexels-photo-16180770.jpeg)", backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-md bg-white text-slate-900 flex items-center justify-center">
              <MapTrifold weight="fill" size={24} />
            </div>
            <div>
              <div className="font-heading font-bold text-2xl tracking-tight">CemTrack</div>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Cement Logistics Control Tower</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 space-y-6 max-w-md">
          <h2 className="font-heading text-4xl font-bold tracking-tight leading-tight">
            Every shipment. One digital ID. Live from plant to site.
          </h2>
          <div className="space-y-3 text-slate-300">
            <Feature icon={Truck} text="GPS tracking via driver phone + checkpoint scans" />
            <Feature icon={Leaf} text="Per-shipment CO₂ & ESG sustainability reporting" />
            <Feature icon={ShieldCheck} text="Multi-tenant SaaS with hard data isolation" />
          </div>
        </div>
        <div className="relative z-10 text-xs text-slate-500">MVP v1 · Rules-based ETA · In-app alerts</div>
      </div>

      {/* right form */}
      <div className="flex items-center justify-center p-6 bg-[#F8F9FA]">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-10 w-10 rounded-md bg-slate-900 text-white flex items-center justify-center">
              <MapTrifold weight="fill" size={22} />
            </div>
            <span className="font-heading font-bold text-xl">CemTrack</span>
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Sign in</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">Access your control tower</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider font-bold text-slate-500">Email</Label>
              <Input id="email" data-testid="login-email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="mt-1.5 bg-white" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider font-bold text-slate-500">Password</Label>
              <Input id="password" data-testid="login-password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5 bg-white" />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={busy}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-200 text-sm text-slate-500">
            New manufacturer?{" "}
            <Dialog open={regOpen} onOpenChange={setRegOpen}>
              <DialogTrigger asChild>
                <button data-testid="open-register-company" className="font-semibold text-slate-900 hover:underline">Register your company</button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading">Register your company</DialogTitle>
                  <DialogDescription>Submit your details — a platform administrator will review and approve before you can sign in.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">Company name</Label>
                    <Input data-testid="reg-company" className="mt-1.5" value={reg.company_name} onChange={(e) => setReg({ ...reg, company_name: e.target.value })} placeholder="Acme Cement Ltd" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">Plant location</Label>
                    <Input data-testid="reg-location" className="mt-1.5" value={reg.plant_location} onChange={(e) => setReg({ ...reg, plant_location: e.target.value })} placeholder="London, England" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">Your name (admin)</Label>
                    <Input data-testid="reg-name" className="mt-1.5" value={reg.admin_name} onChange={(e) => setReg({ ...reg, admin_name: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">Work email</Label>
                    <Input data-testid="reg-email" type="email" className="mt-1.5" value={reg.admin_email} onChange={(e) => setReg({ ...reg, admin_email: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">Password</Label>
                    <Input data-testid="reg-password" type="password" className="mt-1.5" value={reg.admin_password} onChange={(e) => setReg({ ...reg, admin_password: e.target.value })} />
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid="submit-register-company" onClick={submitReg} disabled={regBusy} className="bg-slate-900 hover:bg-slate-800">
                    {regBusy ? "Submitting…" : "Submit for approval"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <span className="mx-1.5 text-slate-300">·</span>
            Receiving shipments?{" "}
            <Dialog open={recOpen} onOpenChange={setRecOpen}>
              <DialogTrigger asChild>
                <button data-testid="open-register-receiver" className="font-semibold text-slate-900 hover:underline">Register as receiver</button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading">Register as receiver</DialogTitle>
                  <DialogDescription>For construction / receiving sites. Get instant access to scan shipment QR codes and confirm deliveries.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">Your name / site name</Label>
                    <Input data-testid="rec-name" className="mt-1.5" value={rec.name} onChange={(e) => setRec({ ...rec, name: e.target.value })} placeholder="Riverside Site" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">Email</Label>
                    <Input data-testid="rec-email" type="email" className="mt-1.5" value={rec.email} onChange={(e) => setRec({ ...rec, email: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">Password</Label>
                    <Input data-testid="rec-password" type="password" className="mt-1.5" value={rec.password} onChange={(e) => setRec({ ...rec, password: e.target.value })} />
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid="submit-register-receiver" onClick={submitRec} disabled={recBusy} className="bg-slate-900 hover:bg-slate-800">
                    {recBusy ? "Creating…" : "Create account & continue"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-3">
      <div className="h-9 w-9 rounded-md bg-white/10 flex items-center justify-center shrink-0">
        <Icon size={18} weight="duotone" />
      </div>
      <span className="text-sm">{text}</span>
    </div>
  );
}
